import { useEffect, useState } from "react";
import {
  getHolidays,
  createHoliday,
  deleteHoliday,
  getHolidayCalendar,
  uploadHolidayCalendar,
  getCompOffData,
  grantCompOff,
  deleteCompOffGrant,
} from "../../api/holidayApi";
import Loader from "../../components/Loader";
import { API_BASE_URL } from "../../api/axios";
import { formatDateDMY } from "../../utils/dateFormatter";

/* ====================================================
   HOLIDAYS TAB (unchanged logic, wrapped in component)
==================================================== */
const HolidaysTab = () => {
  const [holidays, setHolidays] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [calendarFile, setCalendarFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [form, setForm] = useState({ name: "", date: "", description: "" });

  const loadData = async () => {
    try {
      setLoading(true);
      const [holidaysRes, calendarRes] = await Promise.all([
        getHolidays(),
        getHolidayCalendar(),
      ]);
      setHolidays(holidaysRes.data);
      setCalendar(calendarRes.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const uploadCalendar = async (e) => {
    e.preventDefault();
    if (!calendarFile) return;
    const formData = new FormData();
    formData.append("file", calendarFile);
    await uploadHolidayCalendar(formData);
    setCalendarFile(null);
    setSuccessMessage("Holiday calendar uploaded successfully ✅");
    loadData();
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const submitHoliday = async (e) => {
    e.preventDefault();
    await createHoliday(form);
    setForm({ name: "", date: "", description: "" });
    loadData();
  };

  const removeHoliday = async (id) => {
    if (window.confirm("Delete this holiday?")) {
      await deleteHoliday(id);
      loadData();
    }
  };

  return (
    <div>
      {/* CALENDAR UPLOAD */}
      <div className="bg-white p-6 shadow rounded mb-6">
        <h3 className="text-lg font-semibold mb-3">Holiday Calendar</h3>
        {successMessage && (
          <div className="mb-3 text-green-700 bg-green-100 border border-green-300 px-4 py-2 rounded">
            {successMessage}
          </div>
        )}
        <form onSubmit={uploadCalendar} className="flex gap-4 items-center">
          <input
            type="file"
            accept=".pdf,.xlsx,.xls,image/*"
            onChange={(e) => setCalendarFile(e.target.files[0])}
            className="border px-3 py-2 rounded"
            required
          />
          <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">
            Upload Calendar
          </button>
          {calendar?.file && (
            <a
              href={calendar.file.startsWith("http") ? calendar.file : `${API_BASE_URL}${calendar.file}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800 ml-2"
            >
              View Calendar
            </a>
          )}
        </form>
      </div>

      {/* ADD HOLIDAY */}
      <div className="bg-white p-6 shadow rounded mb-6">
        <h3 className="text-lg font-semibold mb-3">Add Additional Holiday</h3>
        <form onSubmit={submitHoliday} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            required
            placeholder="Holiday Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border px-3 py-2 rounded"
          />
          <input
            type="date"
            required
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="border px-3 py-2 rounded"
          />
          <input
            type="text"
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="border px-3 py-2 rounded"
          />
          <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded">
            Add Holiday
          </button>
        </form>
      </div>

      {/* HOLIDAY LIST */}
      {loading ? (
        <Loader />
      ) : (
        <div className="bg-white shadow rounded overflow-x-auto">
          <table className="min-w-full text-sm text-center">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Holiday</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.id} className="border-t">
                  <td className="px-4 py-2">{formatDateDMY(h.date)}</td>
                  <td className="px-4 py-2">{h.name}</td>
                  <td className="px-4 py-2">{h.description || "-"}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => removeHoliday(h.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {holidays.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-6 text-center text-gray-400">
                    No additional holidays added
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ====================================================
   COMP OFF TAB
==================================================== */
const CompOffTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [granting, setGranting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchEmp, setSearchEmp] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const [form, setForm] = useState({
    employees: "all",
    selectedEmails: [],
    comp_off_date: "",
    days: 1,
    reason: "",
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getCompOffData();
      setData(res.data);
    } catch {
      setError("Failed to load Comp Off data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleGrant = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setGranting(true);
    try {
      const payload = {
        comp_off_date: form.comp_off_date,
        days: form.days,
        reason: form.reason,
        employees:
          form.employees === "all"
            ? "all"
            : form.employees === "multiple"
            ? form.selectedEmails
            : form.selectedEmails[0] || "",
      };
      await grantCompOff(payload);
      setSuccess("✅ Comp Off granted successfully!");
      setForm({ employees: "all", selectedEmails: [], comp_off_date: "", days: 1, reason: "" });
      setSearchEmp("");
      loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to grant Comp Off.");
    } finally {
      setGranting(false);
      setTimeout(() => setSuccess(""), 4000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this Comp Off grant? The attendance record will also be removed.")) return;
    setDeletingId(id);
    try {
      await deleteCompOffGrant(id);
      setSuccess("✅ Comp Off grant deleted.");
      loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete Comp Off grant.");
    } finally {
      setDeletingId(null);
      setTimeout(() => setSuccess(""), 4000);
    }
  };

  const toggleEmail = (email) => {
    setForm((prev) => {
      const exists = prev.selectedEmails.includes(email);
      return {
        ...prev,
        selectedEmails: exists
          ? prev.selectedEmails.filter((e) => e !== email)
          : [...prev.selectedEmails, email],
      };
    });
  };

  if (loading) return <Loader />;

  const summary = data?.summary || {};
  const grants = data?.grants || [];

  const filteredGrants = grants.filter(
    (g) =>
      g.name.toLowerCase().includes(searchEmp.toLowerCase()) ||
      g.email.toLowerCase().includes(searchEmp.toLowerCase())
  );


  return (
    <div className="space-y-6">

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4 max-w-md">
        {[
          { label: "Total Employees", value: summary.total_employees ?? 0, color: "from-blue-500 to-blue-700" },
          { label: "Total Granted (Days)", value: summary.total_granted ?? 0, color: "from-purple-500 to-purple-700" },
        ].map((card) => (
          <div
            key={card.label}
            className={`rounded-xl p-4 text-white bg-gradient-to-br ${card.color} shadow-md`}
          >
            <p className="text-xs font-medium opacity-80 uppercase tracking-wide">{card.label}</p>
            <p className="text-3xl font-bold mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      {/* GRANT FORM */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Grant Comp Off</h3>

        {error && (
          <div className="mb-4 px-4 py-2 bg-red-100 border border-red-300 text-red-700 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 px-4 py-2 bg-green-100 border border-green-300 text-green-700 rounded">
            {success}
          </div>
        )}

        <form onSubmit={handleGrant} className="space-y-4">
          {/* Target Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Grant To</label>
            <div className="flex gap-3">
              {[
                { key: "all", label: "All Employees" },
                { key: "single", label: "Single Employee" },
                { key: "multiple", label: "Multiple Employees" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setForm({ ...form, employees: opt.key, selectedEmails: [] })}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                    form.employees === opt.key
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Employee picker */}
          {(form.employees === "single" || form.employees === "multiple") && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Select Employee(s)
              </label>
              <input
                type="text"
                placeholder="Search by name or email…"
                value={searchEmp}
                onChange={(e) => setSearchEmp(e.target.value)}
                className="border px-3 py-2 rounded w-full mb-2 text-sm"
              />
              <div className="max-h-40 overflow-y-auto border rounded divide-y">
                {(data?.employees || [])
                  .filter(
                    (b) =>
                      b.name.toLowerCase().includes(searchEmp.toLowerCase()) ||
                      b.email.toLowerCase().includes(searchEmp.toLowerCase())
                  )
                  .map((b) => {
                    const checked = form.selectedEmails.includes(b.email);
                    return (
                      <label
                        key={b.email}
                        className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                          checked ? "bg-indigo-50" : ""
                        }`}
                      >
                        <input
                          type={form.employees === "single" ? "radio" : "checkbox"}
                          name="emp"
                          checked={checked}
                          onChange={() =>
                            form.employees === "single"
                              ? setForm({ ...form, selectedEmails: [b.email] })
                              : toggleEmail(b.email)
                          }
                        />
                        <span className="text-sm">
                          <span className="font-medium">{b.name}</span>{" "}
                          <span className="text-gray-400 text-xs">({b.email})</span>
                        </span>
                      </label>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Date, Days, Reason */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Comp Off Date</label>
              <input
                type="date"
                required
                value={form.comp_off_date}
                onChange={(e) => setForm({ ...form, comp_off_date: e.target.value })}
                className="border px-3 py-2 rounded w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Comp Off Days</label>
              <input
                type="number"
                min="1"
                max="30"
                required
                value={form.days}
                onChange={(e) => setForm({ ...form, days: e.target.value })}
                className="border px-3 py-2 rounded w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Reason</label>
              <input
                type="text"
                placeholder="e.g., Worked on Sunday"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className="border px-3 py-2 rounded w-full"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={granting}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition disabled:opacity-60"
          >
            {granting ? "Granting…" : "Grant Comp Off"}
          </button>
        </form>
      </div>

      {/* COMP OFF GRANT TABLE */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="text-lg font-semibold text-gray-800">Comp Off Grants</h3>
          <input
            type="text"
            placeholder="Search employee…"
            value={searchEmp}
            onChange={(e) => setSearchEmp(e.target.value)}
            className="border px-3 py-1.5 rounded text-sm w-52"
          />
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-6 py-3 text-left">Employee Name</th>
              <th className="px-6 py-3 text-center">Comp Off Date</th>
              <th className="px-6 py-3 text-center">Comp Off Days</th>
              <th className="px-6 py-3 text-left">Reason</th>
              <th className="px-6 py-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredGrants.map((g) => (
              <tr key={g.id} className="hover:bg-gray-50">
                <td className="px-6 py-3">
                  <p className="font-medium text-gray-800">{g.name}</p>
                  <p className="text-xs text-gray-400">{g.email}</p>
                </td>
                <td className="px-6 py-3 text-center font-medium text-gray-700">
                  {formatDateDMY(g.comp_off_date)}
                </td>
                <td className="px-6 py-3 text-center">
                  <span className="bg-indigo-100 text-indigo-700 px-3 py-0.5 rounded-full text-xs font-bold">
                    {g.days}
                  </span>
                </td>
                <td className="px-6 py-3 text-gray-600">{g.reason || "—"}</td>
                <td className="px-6 py-3 text-center">
                  <button
                    onClick={() => handleDelete(g.id)}
                    disabled={deletingId === g.id}
                    className="bg-red-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-red-700 transition disabled:opacity-60"
                  >
                    {deletingId === g.id ? "Deleting…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
            {filteredGrants.length === 0 && (
              <tr>
                <td colSpan="5" className="py-8 text-center text-gray-400">
                  No Comp Off grants yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ====================================================
   MAIN COMPONENT WITH TABS
==================================================== */
const HolidayManagement = () => {
  const [activeTab, setActiveTab] = useState("holidays");

  const tabs = [
    { key: "holidays", label: "🗓️ Holidays" },
    { key: "compoff", label: "⏱️ Comp Off" },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Holiday Management</h2>

      {/* TAB BAR */}
      <div className="flex gap-2 mb-6 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
              activeTab === tab.key
                ? "border-indigo-600 text-indigo-700 bg-indigo-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "holidays" && <HolidaysTab />}
      {activeTab === "compoff" && <CompOffTab />}
    </div>
  );
};

export default HolidayManagement;
